import { createApp, reactive } from 'vue'

import Form from './submission_form/form'
import DownloadButton from './elements/download_button'
import ToggleSubmit from './elements/toggle_submit'
import FetchForm from './elements/fetch_form'
import ScrollButtons from './elements/scroll_buttons'
import PageContainer from './elements/page_container'
import SubmitForm from './elements/submit_form'
import ModalButton from './elements/modal_button'
import VerifyDropzone from './elements/verify_dropzone'

const safeRegisterElement = (name, element, options = {}) => !window.customElements.get(name) && window.customElements.define(name, element, options)

safeRegisterElement('download-button', DownloadButton)
safeRegisterElement('toggle-submit', ToggleSubmit)
safeRegisterElement('fetch-form', FetchForm)
safeRegisterElement('scroll-buttons', ScrollButtons)
safeRegisterElement('page-container', PageContainer)
safeRegisterElement('submit-form', SubmitForm)
safeRegisterElement('modal-button', ModalButton)
safeRegisterElement('verify-dropzone', VerifyDropzone)
safeRegisterElement('submission-form', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')

    const notifyEmbedParent = (eventName, detail = {}) => {
      if (window.parent === window) return

      const origins = (this.dataset.embedOrigins || this.dataset.embedOrigin || '')
        .split(',')
        .map((origin) => origin.trim())
        .filter(Boolean)

      // Post to every allowed origin; the browser only delivers the message to
      // the parent whose origin matches, so the real host page (dashboard or
      // portal) receives it and the rest are silently dropped.
      origins.forEach((origin) => {
        try {
          window.parent.postMessage({ source: 'esigncenter-form', event: eventName, detail }, origin)
        } catch (_error) {
          // Ignore an unparseable target origin and continue with the others.
        }
      })
    }

    this.app = createApp(Form, {
      submitter: JSON.parse(this.dataset.submitter),
      inviteSubmitters: JSON.parse(this.dataset.inviteSubmitters),
      optionalInviteSubmitters: JSON.parse(this.dataset.optionalInviteSubmitters),
      schema: JSON.parse(this.dataset.schema),
      canSendEmail: this.dataset.canSendEmail === 'true',
      previousSignatureValue: this.dataset.previousSignatureValue,
      goToLast: this.dataset.goToLast === 'true',
      isDemo: this.dataset.isDemo === 'true',
      scrollPadding: this.dataset.scrollPadding || '-80px',
      orderAsOnPage: this.dataset.orderAsOnPage !== 'false',
      signatureText: this.dataset.signatureText,
      language: this.dataset.language,
      dryRun: this.dataset.dryRun === 'true',
      expand: ['true', 'false'].includes(this.dataset.expand) ? this.dataset.expand === 'true' : null,
      withSignatureId: this.dataset.withSignatureId === 'true',
      requireSigningReason: this.dataset.requireSigningReason === 'true',
      withConfetti: this.dataset.withConfetti === 'true',
      withFieldLabels: this.dataset.withFieldLabels !== 'false',
      // ESIGN consent contract from the Rails partial (strings included, so
      // i18n.yml stays the single source). A mount without the attribute never
      // blocks; every real mount carries it (asserted by the system spec).
      esignConsent: JSON.parse(this.dataset.esignConsent || '{"consented": true}'),
      reuseSignature: this.dataset.reuseSignature !== 'false',
      withTypedSignature: this.dataset.withTypedSignature !== 'false',
      authenticityToken: document.querySelector('meta[name="csrf-token"]')?.content,
      rememberSignature: this.dataset.rememberSignature === 'true',
      values: reactive(JSON.parse(this.dataset.values)),
      completedButton: JSON.parse(this.dataset.completedButton || '{}'),
      withQrButton: true,
      completedMessage: JSON.parse(this.dataset.completedMessage || '{}'),
      completedRedirectUrl: this.dataset.completedRedirectUrl,
      attachments: reactive(JSON.parse(this.dataset.attachments)),
      fields: JSON.parse(this.dataset.fields),
      completeButtonContainer: document.getElementById('complete_button_container'),
      completeButtonScrollContainer: document.getElementById('complete_button_container_scroll'),
      onComplete: (data = {}) => notifyEmbedParent('completed', {
        submitter: data.submitter || JSON.parse(this.dataset.submitter),
        signing_session: data.signing_session,
        response: data
      })
    })

    this.app.mount(this.appElem)

    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})
